// ============================================
// COMMUNITY HUB — REUSABLE COMPONENTS
// ============================================

import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import {
  Star, Plus, X, AlertCircle, CheckCircle, Image as ImageIcon, ArrowLeft,
  Heart, MessageSquare, Trash2, Pin, Share2, Search, Users, Clock,
  TrendingUp, Paperclip, Globe, Lock, Edit2, MapPin, Calendar,
  DollarSign, Link2, ExternalLink, Eye, EyeOff, Filter, SlidersHorizontal,
  ChevronDown, ChevronUp, MoreHorizontal, Send, AlertTriangle,
  UserPlus, UserMinus, Shield, ShieldAlert, Crown, BadgeCheck,
  Loader2, Upload, FileText, Video, Mic, BarChart3, Coins,
} from 'lucide-react';
import type {
  Community, Post, Comment, User, PermissionMap,
  CommunityMember, SocialLink, SocialPlatform, ToastNotification,
  SortOption, FilterOption, CommunityCategory, PostType,
  Poll, CommunityEvent, EarningsSummary,
} from './types';
import { COMMUNITY_CATEGORIES, PRO_SOCIAL_PLATFORMS, ENTERPRISE_SOCIAL_PLATFORMS } from './types';
import { useSocialPlatforms, useCommission } from './hooks';
import {
  formatRelativeTime, formatFullDate, formatCount, formatCurrency,
  getInitials, generateAvatarColor, truncateText, sanitizeText,
  validateFile, sortPosts,
} from './utils';

// ─── NOTIFICATION TOAST ────────────────────
export const Toast: React.FC<{
  toast: ToastNotification;
  onRemove: (id: string) => void;
}> = memo(({ toast, onRemove }) => {
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    if (toast.duration && toast.duration > 0) {
      const timer = setTimeout(() => setExiting(true), toast.duration - 300);
      return () => clearTimeout(timer);
    }
  }, [toast.duration]);

  const bgMap = {
    success: 'var(--toast-success-bg, #ecfdf5)',
    error: 'var(--toast-error-bg, #fef2f2)',
    warning: 'var(--toast-warning-bg, #fffbeb)',
    info: 'var(--toast-info-bg, #eff6ff)',
  };

  const colorMap = {
    success: 'var(--toast-success-color, #065f46)',
    error: 'var(--toast-error-color, #991b1b)',
    warning: 'var(--toast-warning-color, #92400e)',
    info: 'var(--toast-info-color, #1e40af)',
  };

  const IconMap = {
    success: CheckCircle,
    error: AlertCircle,
    warning: AlertTriangle,
    info: AlertCircle,
  };

  const Icon = IconMap[toast.type];

  return (
    <div
      className={`community-toast community-toast--${toast.type} ${exiting ? 'community-toast--exit' : ''}`}
      style={{
        padding: '14px 18px',
        borderRadius: '12px',
        background: bgMap[toast.type],
        color: colorMap[toast.type],
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        fontSize: '14px',
        fontWeight: '500',
        border: `1px solid ${colorMap[toast.type]}20`,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        pointerEvents: 'auto',
        maxWidth: '420px',
      }}
      role="alert"
      aria-live="polite"
    >
      <Icon size={18} />
      <span style={{ flex: 1 }}>{toast.message}</span>
      <button
        onClick={() => onRemove(toast.id)}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'inherit',
          padding: '4px',
          borderRadius: '6px',
        }}
        aria-label="Dismiss notification"
      >
        <X size={16} />
      </button>
    </div>
  );
});

Toast.displayName = 'Toast';

// ─── TOAST CONTAINER ───────────────────────
export const ToastContainer: React.FC<{
  toasts: ToastNotification[];
  onRemove: (id: string) => void;
}> = memo(({ toasts, onRemove }) => {
  if (toasts.length === 0) return null;

  return (
    <div
      className="community-toast-container"
      style={{
        position: 'fixed',
        top: '24px',
        right: '24px',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        pointerEvents: 'none',
      }}
      aria-live="polite"
      aria-label="Notifications"
    >
      {toasts.map(toast => (
        <Toast key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>
  );
});

ToastContainer.displayName = 'ToastContainer';

// ─── AVATAR ────────────────────────────────
export const Avatar: React.FC<{
  name: string;
  src?: string;
  size?: number;
  className?: string;
}> = memo(({ name, src, size = 36, className = '' }) => {
  const bgColor = generateAvatarColor(name);
  const initials = getInitials(name);

  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className={`community-avatar ${className}`}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          objectFit: 'cover',
          flexShrink: 0,
        }}
        loading="lazy"
      />
    );
  }

  return (
    <div
      className={`community-avatar community-avatar--placeholder ${className}`}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: bgColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontWeight: 'bold',
        fontSize: size * 0.4,
        flexShrink: 0,
      }}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
});

Avatar.displayName = 'Avatar';

// ─── BADGE ─────────────────────────────────
export const Badge: React.FC<{
  text: string;
  color?: string;
  bgColor?: string;
  icon?: React.ReactNode;
}> = memo(({ text, color = '#6b7280', bgColor = '#f3f4f6', icon }) => {
  return (
    <span
      className="community-badge"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '3px 10px',
        borderRadius: '20px',
        fontSize: '11px',
        fontWeight: '600',
        color,
        background: bgColor,
        whiteSpace: 'nowrap',
      }}
    >
      {icon}
      {text}
    </span>
  );
});

Badge.displayName = 'Badge';

// ─── SKELETON LOADER ───────────────────────
export const Skeleton: React.FC<{
  width?: string | number;
  height?: string | number;
  borderRadius?: string | number;
  className?: string;
}> = memo(({ width = '100%', height = '20px', borderRadius = '8px', className = '' }) => {
  return (
    <div
      className={`community-skeleton ${className}`}
      style={{
        width,
        height,
        borderRadius,
        background: 'linear-gradient(90deg, var(--skeleton-base, #e5e7eb) 25%, var(--skeleton-shine, #f3f4f6) 50%, var(--skeleton-base, #e5e7eb) 75%)',
        backgroundSize: '200% 100%',
        animation: 'skeletonShimmer 1.5s ease-in-out infinite',
      }}
      aria-hidden="true"
    />
  );
});

Skeleton.displayName = 'Skeleton';

// ─── EMPTY STATE ───────────────────────────
export const EmptyState: React.FC<{
  icon: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
}> = memo(({ icon, title, description, action }) => {
  return (
    <div
      className="community-empty-state"
      style={{
        textAlign: 'center',
        padding: '60px 20px',
        background: 'var(--bg-primary, white)',
        borderRadius: '14px',
        border: '2px dashed var(--border-color, #e5e7eb)',
      }}
    >
      <div style={{ color: 'var(--text-muted, #d1d5db)', marginBottom: '16px' }}>
        {icon}
      </div>
      <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px', color: 'var(--text-primary, #111827)' }}>
        {title}
      </h3>
      {description && (
        <p style={{ color: 'var(--text-secondary, #9ca3af)', marginBottom: '16px', fontSize: '14px' }}>
          {description}
        </p>
      )}
      {action}
    </div>
  );
});

EmptyState.displayName = 'EmptyState';

// ─── MODAL ─────────────────────────────────
export const Modal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxWidth?: string;
}> = memo(({ isOpen, onClose, title, children, footer, maxWidth = '480px' }) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Focus trap
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      if (e.key === 'Tab' && contentRef.current) {
        const focusable = contentRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    // Focus first focusable element
    setTimeout(() => {
      const firstFocusable = contentRef.current?.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      firstFocusable?.focus();
    }, 100);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      className="community-modal-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        backdropFilter: 'blur(4px)',
        padding: '20px',
      }}
      onClick={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        ref={contentRef}
        className="community-modal-content"
        style={{
          background: 'var(--bg-primary, white)',
          borderRadius: '18px',
          width: '100%',
          maxWidth,
          maxHeight: '85vh',
          overflowY: 'auto',
          boxShadow: '0 25px 60px rgba(0,0,0,0.2)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '24px 24px 0',
          }}
        >
          <h2 style={{ fontSize: '20px', fontWeight: '700', margin: 0, color: 'var(--text-primary, #111827)' }}>
            {title}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text-secondary, #6b7280)',
              padding: '6px',
              borderRadius: '8px',
              transition: 'background 0.15s',
            }}
            aria-label="Close modal"
          >
            <X size={22} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px' }}>
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div
            style={{
              padding: '16px 24px',
              borderTop: '1px solid var(--border-color, #e5e7eb)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
});

Modal.displayName = 'Modal';

// ─── COMMUNITY CARD ────────────────────────
export const CommunityCard: React.FC<{
  community: Community;
  onClick: () => void;
}> = memo(({ community, onClick }) => {
  const avatarColor = generateAvatarColor(community.name);

  return (
    <div
      className="community-card"
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      tabIndex={0}
      role="button"
      aria-label={`View ${community.name} community`}
      style={{
        padding: '24px',
        background: 'var(--bg-primary, white)',
        border: '1.5px solid var(--border-color, #e5e7eb)',
        borderRadius: '16px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      }}
    >
      {/* Avatar */}
      <div
        style={{
          width: '52px',
          height: '52px',
          borderRadius: '14px',
          background: `linear-gradient(135deg, ${avatarColor}, ${avatarColor}dd)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontWeight: 'bold',
          fontSize: '22px',
          marginBottom: '14px',
        }}
      >
        {community.name.charAt(0).toUpperCase()}
      </div>

      {/* Name & Category */}
      <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '4px', color: 'var(--text-primary, #111827)' }}>
        {community.name}
      </h3>
      <Badge text={community.category || 'General'} />

      {/* Description */}
      <p style={{
        fontSize: '13px',
        color: 'var(--text-secondary, #6b7280)',
        marginTop: '10px',
        marginBottom: '14px',
        lineHeight: '1.5',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}>
        {community.description || 'No description yet.'}
      </p>

      {/* Stats */}
      <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: 'var(--text-muted, #9ca3af)' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <Users size={14} /> {formatCount(community.memberCount || 0)}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          {community.visibility === 'private' ? <Lock size={14} /> : <Globe size={14} />}
          {community.visibility === 'private' ? 'Private' : 'Public'}
        </span>
        {community.isMonetized && (
          <span style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#f59e0b' }}>
            <DollarSign size={14} /> Paid
          </span>
        )}
      </div>

      {/* Tags */}
      {community.tags && community.tags.length > 0 && (
        <div style={{ display: 'flex', gap: '6px', marginTop: '12px', flexWrap: 'wrap' }}>
          {community.tags.slice(0, 3).map(tag => (
            <span
              key={tag}
              style={{
                padding: '3px 8px',
                background: 'var(--tag-bg, #f3f4f6)',
                borderRadius: '6px',
                fontSize: '10px',
                color: 'var(--text-muted, #6b7280)',
              }}
            >
              #{tag}
            </span>
          ))}
          {community.tags.length > 3 && (
            <span style={{ fontSize: '10px', color: 'var(--text-muted, #9ca3af)' }}>
              +{community.tags.length - 3}
            </span>
          )}
        </div>
      )}
    </div>
  );
});

CommunityCard.displayName = 'CommunityCard';

// ─── POST CARD ─────────────────────────────
export const PostCard: React.FC<{
  post: Post;
  currentUserId?: string | null;
  isAdmin: boolean;
  isMember: boolean;
  onLike: (postId: string) => void;
  onComment: (postId: string) => void;
  onPin: (postId: string) => void;
  onDelete: (postId: string) => void;
  onEdit: (postId: string, content: string) => void;
  onVote: (postId: string, optionId: string) => void;
  onRSVP: (postId: string) => void;
  onShare: () => void;
  onReport: (postId: string) => void;
}> = memo(({
  post,
  currentUserId,
  isAdmin,
  isMember,
  onLike,
  onComment,
  onPin,
  onDelete,
  onEdit,
  onVote,
  onRSVP,
  onShare,
  onReport,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(post.content || '');
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [imageLoaded, setImageLoaded] = useState(false);

  const authorId = typeof post.author === 'string' ? post.author : post.author?._id;
  const authorName = typeof post.author === 'string' ? 'Unknown' : post.author?.name || 'Unknown';
  const isOwnPost = currentUserId === authorId;

  const handleSaveEdit = () => {
    if (editContent.trim()) {
      onEdit(post._id, editContent.trim());
      setIsEditing(false);
    }
  };

  const handleCommentSubmit = () => {
    if (commentText.trim()) {
      onComment(post._id);
      setCommentText('');
    }
  };

  return (
    <div
      className={`community-post-card ${post.pinned ? 'community-post-card--pinned' : ''}`}
      style={{
        background: 'var(--bg-primary, white)',
        padding: '20px',
        borderRadius: '14px',
        border: `1px solid ${post.pinned ? '#fbbf24' : 'var(--border-color, #e5e7eb)'}`,
        marginBottom: '12px',
      }}
    >
      {/* Pinned indicator */}
      {post.pinned && (
        <div style={{ color: '#d97706', fontSize: '11px', fontWeight: '600', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Pin size={12} /> Pinned Post
        </div>
      )}

      {/* Author row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
        <Avatar name={authorName} size={38} />
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <strong style={{ fontSize: '14px', color: 'var(--text-primary, #111827)' }}>
              {authorName}
            </strong>
            {post.author?.plan === 'enterprise' && (
              <Badge text="Enterprise" color="#7c3aed" bgColor="#ede9fe" icon={<Crown size={10} />} />
            )}
            {post.author?.plan === 'pro' && (
              <Badge text="Pro" color="#2563eb" bgColor="#dbeafe" icon={<BadgeCheck size={10} />} />
            )}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted, #9ca3af)', marginTop: '2px' }}>
            {formatRelativeTime(post.createdAt)}
            {post.edited && <span style={{ marginLeft: '6px' }}>(edited)</span>}
          </div>
        </div>

        {/* Actions menu */}
        {(isOwnPost || isAdmin) && (
          <div style={{ display: 'flex', gap: '4px' }}>
            {isOwnPost && (
              <button
                onClick={() => { setIsEditing(true); setEditContent(post.content || ''); }}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--text-muted, #9ca3af)', padding: '4px', borderRadius: '6px',
                }}
                aria-label="Edit post"
              >
                <Edit2 size={14} />
              </button>
            )}
            {isAdmin && (
              <button
                onClick={() => onPin(post._id)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: post.pinned ? '#d97706' : 'var(--text-muted, #9ca3af)',
                  padding: '4px', borderRadius: '6px',
                }}
                aria-label={post.pinned ? 'Unpin post' : 'Pin post'}
              >
                <Pin size={14} />
              </button>
            )}
            {(isOwnPost || isAdmin) && (
              <button
                onClick={() => {
                  if (window.confirm('Delete this post? This action can be undone within 30 days.')) {
                    onDelete(post._id);
                  }
                }}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#ef4444', padding: '4px', borderRadius: '6px',
                }}
                aria-label="Delete post"
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        )}
      </div>
             {/* Content */}
      {post.type === 'post' && (
        isEditing ? (
          <div style={{ marginBottom: '12px' }}>
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: '1.5px solid var(--border-color, #e5e7eb)',
                borderRadius: '10px',
                resize: 'vertical',
                fontSize: '14px',
                minHeight: '80px',
                fontFamily: 'inherit',
                background: 'var(--bg-secondary, #f9fafb)',
                color: 'var(--text-primary, #111827)',
              }}
              autoFocus
            />
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <button
                onClick={handleSaveEdit}
                disabled={!editContent.trim()}
                style={{
                  padding: '8px 16px',
                  background: editContent.trim() ? 'var(--accent, #7c3aed)' : '#d1d5db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: editContent.trim() ? 'pointer' : 'not-allowed',
                  fontSize: '12px',
                  fontWeight: '600',
                }}
              >
                Save
              </button>
              <button
                onClick={() => setIsEditing(false)}
                style={{
                  padding: '8px 16px',
                  background: 'var(--bg-secondary, #f3f4f6)',
                  color: 'var(--text-secondary, #6b7280)',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div
            style={{
              fontSize: '14px',
              lineHeight: '1.7',
              marginBottom: '12px',
              whiteSpace: 'pre-wrap',
              color: 'var(--text-primary, #1f2937)',
              wordBreak: 'break-word',
            }}
          >
            {String(post.content || '').split(/(!\[Image\]\(.*?\))/g).map((part, i) => {
              const match = part.match(/!\[Image\]\((.*?)\)/);
              if (match) {
                return <img key={i} src={match[1]} alt="Uploaded" style={{ maxWidth: '100%', borderRadius: '10px', margin: '10px 0' }} />;
              }
              return <span key={i}>{part}</span>;
            })}
          </div>
        )
      )} 

      {/* Media */}
      {post.mediaUrls && post.mediaUrls.length > 0 && (
        <div style={{ marginBottom: '12px' }}>
          {post.mediaUrls.map((url, i) => (
            <div key={i} style={{ position: 'relative', marginBottom: '8px' }}>
              {!imageLoaded && (
                <Skeleton height="250px" borderRadius="10px" />
              )}
              <img
                src={url}
                alt={`Post image ${i + 1}`}
                style={{
                  maxWidth: '100%',
                  borderRadius: '10px',
                  display: imageLoaded ? 'block' : 'none',
                }}
                loading="lazy"
                onLoad={() => setImageLoaded(true)}
              />
            </div>
          ))}
        </div>
      )}

      {/* Poll */}
      {post.type === 'poll' && post.poll && <PollDisplay poll={post.poll} postId={post._id} onVote={onVote} />}

      {/* Event */}
      {post.type === 'event' && post.event && <EventDisplay event={post.event} postId={post._id} onRSVP={onRSVP} />}

      {/* Action bar */}
      <div style={{
        display: 'flex',
        gap: '20px',
        paddingTop: '12px',
        borderTop: '1px solid var(--border-color-light, #f3f4f6)',
        flexWrap: 'wrap',
      }}>
        <ActionButton icon={<Heart size={15} />} count={post.likes?.length || 0} onClick={() => onLike(post._id)} active={post.likes?.includes(currentUserId || '')} label="Like" />
        <ActionButton icon={<MessageSquare size={15} />} count={post.commentCount || post.comments?.length || 0} onClick={() => setShowComments(!showComments)} active={showComments} label="Comment" />
        <button
          onClick={onShare}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: '13px', color: 'var(--text-muted, #6b7280)',
            display: 'flex', alignItems: 'center', gap: '5px', padding: '4px 8px',
            borderRadius: '8px',
          }}
          aria-label="Share post"
        >
          <Share2 size={15} /> Share
        </button>
        <button
          onClick={() => onReport(post._id)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: '13px', color: 'var(--text-muted, #9ca3af)',
            display: 'flex', alignItems: 'center', gap: '5px', padding: '4px 8px',
            borderRadius: '8px', marginLeft: 'auto',
          }}
          aria-label="Report post"
        >
          <AlertTriangle size={14} />
        </button>
      </div>

      {/* Comments section */}
      {showComments && (
        <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1px solid var(--border-color-light, #f3f4f6)' }}>
          {post.comments && post.comments.length > 0 ? (
            post.comments.slice(0, 5).map((comment, i) => (
              <CommentItem key={comment._id || i} comment={comment} />
            ))
          ) : (
            <p style={{ fontSize: '13px', color: 'var(--text-muted, #9ca3af)', textAlign: 'center', padding: '12px' }}>
              No comments yet. Be the first!
            </p>
          )}

          {isMember && (
            <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
              <input
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleCommentSubmit();
                  }
                }}
                placeholder="Write a comment..."
                style={{
                  flex: 1,
                  padding: '10px 14px',
                  border: '1.5px solid var(--border-color, #e5e7eb)',
                  borderRadius: '24px',
                  fontSize: '13px',
                  outline: 'none',
                  background: 'var(--bg-secondary, #f9fafb)',
                  color: 'var(--text-primary, #111827)',
                }}
                aria-label="Comment input"
              />
              <button
                onClick={handleCommentSubmit}
                disabled={!commentText.trim()}
                style={{
                  padding: '10px 18px',
                  background: commentText.trim() ? 'var(--accent, #7c3aed)' : '#d1d5db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '24px',
                  cursor: commentText.trim() ? 'pointer' : 'not-allowed',
                  fontSize: '13px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <Send size={14} /> Reply
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

PostCard.displayName = 'PostCard';

// ─── ACTION BUTTON ─────────────────────────
const ActionButton: React.FC<{
  icon: React.ReactNode;
  count: number;
  onClick: () => void;
  active?: boolean;
  label: string;
}> = memo(({ icon, count, onClick, active, label }) => (
  <button
    onClick={onClick}
    style={{
      background: active ? 'var(--accent-light, #f5f3ff)' : 'none',
      border: 'none',
      cursor: 'pointer',
      fontSize: '13px',
      color: active ? 'var(--accent, #7c3aed)' : 'var(--text-muted, #6b7280)',
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
      padding: '6px 10px',
      borderRadius: '8px',
      fontWeight: active ? '600' : '400',
      transition: 'all 0.15s',
    }}
    aria-label={`${label} (${count})`}
  >
    {icon} {count > 0 && count}
  </button>
));

ActionButton.displayName = 'ActionButton';

// ─── COMMENT ITEM ──────────────────────────
const CommentItem: React.FC<{ comment: Comment }> = memo(({ comment }) => {
  const authorName = typeof comment.author === 'string' ? 'Unknown' : comment.author?.name || 'Unknown';

  return (
    <div style={{ padding: '10px', background: 'var(--bg-secondary, #f9fafb)', borderRadius: '10px', marginBottom: '6px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
        <Avatar name={authorName} size={24} />
        <strong style={{ fontSize: '12px' }}>{authorName}</strong>
        <span style={{ fontSize: '10px', color: 'var(--text-muted, #9ca3af)' }}>
          {formatRelativeTime(comment.createdAt)}
        </span>
      </div>
      <p style={{ fontSize: '13px', margin: 0, paddingLeft: '32px', color: 'var(--text-primary, #1f2937)' }}>
        {comment.content}
      </p>
    </div>
  );
});

CommentItem.displayName = 'CommentItem';

// ─── POLL DISPLAY ──────────────────────────
export const PollDisplay: React.FC<{
  poll: Poll;
  postId: string;
  onVote: (postId: string, optionId: string) => void;
}> = memo(({ poll, postId, onVote }) => {
  const totalVotes = poll.totalVotes || poll.options.reduce((sum, opt) => sum + (opt.votes?.length || 0), 0);

  return (
    <div style={{ marginBottom: '14px', padding: '14px', background: 'var(--bg-secondary, #f9fafb)', borderRadius: '10px' }}>
      <p style={{ fontWeight: '600', marginBottom: '10px', fontSize: '15px' }}>{poll.question}</p>
      <p style={{ fontSize: '11px', color: 'var(--text-muted, #9ca3af)', marginBottom: '10px' }}>
        {totalVotes} total vote{totalVotes !== 1 ? 's' : ''}
        {poll.expiresAt && ` · Ends ${formatRelativeTime(poll.expiresAt)}`}
      </p>
      {poll.options.map((option) => {
        const percentage = totalVotes > 0 ? ((option.votes?.length || 0) / totalVotes) * 100 : 0;
        return (
          <button
            key={option.id}
            onClick={() => onVote(postId, option.id)}
            style={{
              width: '100%',
              padding: '12px 14px',
              marginBottom: '8px',
              background: `linear-gradient(90deg, var(--accent, #7c3aed) ${percentage}%, var(--bg-primary, #e5e7eb) ${percentage}%)`,
              border: '1px solid var(--border-color, #e5e7eb)',
              borderRadius: '10px',
              cursor: 'pointer',
              textAlign: 'left',
              fontSize: '13px',
              fontWeight: '500',
              color: 'var(--text-primary, #1f2937)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.2s',
            }}
          >
            <span style={{ position: 'relative', zIndex: 1 }}>
              {option.text}
              <span style={{ float: 'right', opacity: 0.8 }}>
                {Math.round(percentage)}% ({option.votes?.length || 0})
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
});

PollDisplay.displayName = 'PollDisplay';

// ─── EVENT DISPLAY ─────────────────────────
export const EventDisplay: React.FC<{
  event: CommunityEvent;
  postId: string;
  onRSVP: (postId: string) => void;
}> = memo(({ event, postId, onRSVP }) => {
  const attendeeCount = event.attendees?.length || 0;
  const isFull = event.attendeeLimit ? attendeeCount >= event.attendeeLimit : false;

  return (
    <div style={{ marginBottom: '14px', padding: '16px', background: 'var(--bg-secondary, #f9fafb)', borderRadius: '12px', border: '1px solid var(--border-color-light, #f3f4f6)' }}>
      <h4 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '8px', color: 'var(--text-primary, #111827)' }}>
        📅 {event.title}
      </h4>
      {event.description && (
        <p style={{ fontSize: '13px', color: 'var(--text-secondary, #6b7280)', marginBottom: '10px' }}>
          {truncateText(event.description, 150)}
        </p>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px', color: 'var(--text-secondary, #6b7280)', marginBottom: '12px' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Calendar size={14} /> {formatFullDate(event.date)}
          {event.time && ` at ${event.time}`}
        </span>
        {event.location && (
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <MapPin size={14} /> {event.location}
          </span>
        )}
        {event.virtualLink && (
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Video size={14} /> <a href={event.virtualLink} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent, #7c3aed)' }}>Join virtual meeting</a>
          </span>
        )}
        {event.attendeeLimit && (
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Users size={14} /> {attendeeCount}/{event.attendeeLimit} attendees
          </span>
        )}
      </div>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <button
          onClick={() => onRSVP(postId)}
          disabled={isFull}
          style={{
            padding: '10px 20px',
            background: isFull ? '#d1d5db' : 'var(--accent, #7c3aed)',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            cursor: isFull ? 'not-allowed' : 'pointer',
            fontSize: '13px',
            fontWeight: '600',
          }}
        >
          {isFull ? 'Full' : `RSVP (${attendeeCount})`}
        </button>
        {event.isPaid && event.ticketPrice && (
          <Badge text={formatCurrency(event.ticketPrice)} color="#f59e0b" bgColor="#fef3c7" icon={<DollarSign size={10} />} />
        )}
      </div>
    </div>
  );
});

EventDisplay.displayName = 'EventDisplay';

// ─── SEARCH & FILTER BAR ───────────────────
export const SearchFilterBar: React.FC<{
  searchTerm: string;
  onSearchChange: (value: string) => void;
  activeFilter: FilterOption;
  onFilterChange: (filter: FilterOption) => void;
  activeCategory: string;
  onCategoryChange: (category: string) => void;
}> = memo(({ searchTerm, onSearchChange, activeFilter, onFilterChange, activeCategory, onCategoryChange }) => {
  const filters: { key: FilterOption; label: string; icon: React.ReactNode }[] = [
    { key: 'all', label: 'All', icon: null },
    { key: 'popular', label: 'Popular', icon: <TrendingUp size={14} /> },
    { key: 'newest', label: 'Newest', icon: <Clock size={14} /> },
    { key: 'trending', label: 'Trending', icon: <Star size={14} /> },
  ];

  const categories = [
    { label: 'All', value: 'all' },
    ...COMMUNITY_CATEGORIES.map(c => ({ label: c.charAt(0).toUpperCase() + c.slice(1), value: c })),
  ];

  return (
    <div style={{ marginBottom: '20px' }}>
      {/* Search + Filters row */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: '250px', position: 'relative' }}>
          <Search
            size={18}
            style={{
              position: 'absolute',
              left: '14px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-muted, #9ca3af)',
              pointerEvents: 'none',
            }}
          />
          <input
            type="search"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search communities..."
            style={{
              width: '100%',
              padding: '12px 16px 12px 44px',
              border: '1.5px solid var(--border-color, #e5e7eb)',
              borderRadius: '12px',
              fontSize: '14px',
              outline: 'none',
              background: 'var(--bg-primary, white)',
              color: 'var(--text-primary, #111827)',
              transition: 'border-color 0.2s',
              boxSizing: 'border-box',
            }}
            aria-label="Search communities"
          />
        </div>
        {filters.map(f => (
          <button
            key={f.key}
            onClick={() => onFilterChange(f.key)}
            style={{
              padding: '10px 18px',
              background: activeFilter === f.key ? 'var(--accent-light, #f5f3ff)' : 'var(--bg-secondary, #f9fafb)',
              color: activeFilter === f.key ? 'var(--accent, #7c3aed)' : 'var(--text-secondary, #6b7280)',
              border: `1.5px solid ${activeFilter === f.key ? 'var(--accent-border, #c4b5fd)' : 'var(--border-color, #e5e7eb)'}`,
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: activeFilter === f.key ? '600' : '400',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              whiteSpace: 'nowrap',
            }}
          >
            {f.icon}{f.label}
          </button>
        ))}
      </div>

      {/* Categories row */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {categories.map(cat => (
          <button
            key={cat.value}
            onClick={() => onCategoryChange(cat.value)}
            style={{
              padding: '7px 14px',
              background: activeCategory === cat.value ? 'var(--accent, #7c3aed)' : 'var(--bg-secondary, #f3f4f6)',
              color: activeCategory === cat.value ? 'white' : 'var(--text-secondary, #6b7280)',
              border: 'none',
              borderRadius: '20px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: activeCategory === cat.value ? '600' : '400',
              textTransform: 'capitalize',
              transition: 'all 0.15s',
            }}
          >
            {cat.label}
          </button>
        ))}
      </div>
    </div>
  );
});

SearchFilterBar.displayName = 'SearchFilterBar';

// ─── SORT BAR ──────────────────────────────
export const SortBar: React.FC<{
  activeSort: SortOption;
  onSortChange: (sort: SortOption) => void;
}> = memo(({ activeSort, onSortChange }) => {
  const sortOptions: { key: SortOption; label: string }[] = [
    { key: 'latest', label: 'Latest' },
    { key: 'top', label: 'Top' },
    { key: 'pinned', label: 'Pinned' },
    { key: 'most_commented', label: 'Most Discussed' },
    { key: 'my_posts', label: 'My Posts' },
  ];

  return (
    <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
      {sortOptions.map(opt => (
        <button
          key={opt.key}
          onClick={() => onSortChange(opt.key)}
          style={{
            padding: '8px 16px',
            background: activeSort === opt.key ? 'var(--accent-light, #f5f3ff)' : 'var(--bg-secondary, #f9fafb)',
            color: activeSort === opt.key ? 'var(--accent, #7c3aed)' : 'var(--text-secondary, #6b7280)',
            border: `1px solid ${activeSort === opt.key ? 'var(--accent-border, #c4b5fd)' : 'var(--border-color, #e5e7eb)'}`,
            borderRadius: '24px',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: activeSort === opt.key ? '600' : '400',
            transition: 'all 0.15s',
          }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
});

SortBar.displayName = 'SortBar';

// ─── CREATE COMMUNITY MODAL ────────────────
export const CreateCommunityModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  permissions: PermissionMap;
  userPlan: string;
}> = memo(({ isOpen, onClose, onSubmit, permissions, userPlan }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<CommunityCategory>('general');
  const [visibility, setVisibility] = useState<'public' | 'private'>('public');
  const [isMonetized, setIsMonetized] = useState(false);
  const [subscriptionPrice, setSubscriptionPrice] = useState(0);
  const [tags, setTags] = useState('');

  const handleSubmit = () => {
    if (!name.trim()) return;
    onSubmit({
      name: name.trim(),
      description: description.trim(),
      category,
      visibility,
      isMonetized,
      subscriptionPrice: isMonetized ? subscriptionPrice * 100 : 0, // convert to cents
      tags: tags.split(',').map(t => t.trim()).filter(Boolean),
    });
    // Reset form
    setName('');
    setDescription('');
    setCategory('general');
    setVisibility('public');
    setIsMonetized(false);
    setSubscriptionPrice(0);
    setTags('');
    onClose();
  };

 if (!permissions.canCreateCommunity && userPlan !== 'enterprise') { 
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Create Community">
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Lock size={48} color="var(--text-muted, #d1d5db)" />
          <h3 style={{ marginTop: '16px', color: 'var(--text-primary, #111827)' }}>
            {userPlan === 'starter'
              ? 'Upgrade to Pro to create communities'
              : 'Community limit reached'}
          </h3>
          <p style={{ color: 'var(--text-secondary, #6b7280)', fontSize: '14px', marginBottom: '20px' }}>
            {userPlan === 'starter'
              ? 'Starter users can only view communities. Upgrade to Pro to create up to 4 communities.'
              : `You've reached your limit of ${permissions.maxCommunities} communities. Upgrade to Enterprise for unlimited.`}
          </p>
          <button
            style={{
              padding: '12px 24px',
              background: 'var(--accent, #7c3aed)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px',
            }}
          >
            {userPlan === 'starter' ? 'Upgrade to Pro' : 'Upgrade to Enterprise'}
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create Community"
      footer={
        <>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              background: 'var(--bg-secondary, #f3f4f6)',
              color: 'var(--text-secondary, #6b7280)',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!name.trim()}
            style={{
              padding: '10px 24px',
              background: name.trim() ? 'var(--accent, #7c3aed)' : '#d1d5db',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: name.trim() ? 'pointer' : 'not-allowed',
              fontWeight: '600',
              fontSize: '14px',
            }}
          >
            Create ({permissions.communitiesRemaining} remaining)
          </button>
        </>
      }
    >
      {/* Name */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: 'var(--text-primary, #111827)' }}>
          Community Name *
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Digital Marketing Pros"
          maxLength={100}
          style={{
            width: '100%',
            padding: '12px',
            border: '1.5px solid var(--border-color, #d1d5db)',
            borderRadius: '10px',
            fontSize: '14px',
            outline: 'none',
            background: 'var(--bg-secondary, #f9fafb)',
            color: 'var(--text-primary, #111827)',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Description */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: 'var(--text-primary, #111827)' }}>
          Description
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What's this community about?"
          maxLength={500}
          rows={3}
          style={{
            width: '100%',
            padding: '12px',
            border: '1.5px solid var(--border-color, #d1d5db)',
            borderRadius: '10px',
            fontSize: '14px',
            outline: 'none',
            resize: 'vertical',
            background: 'var(--bg-secondary, #f9fafb)',
            color: 'var(--text-primary, #111827)',
            boxSizing: 'border-box',
          }}
        />
        <span style={{ fontSize: '11px', color: 'var(--text-muted, #9ca3af)', float: 'right' }}>
          {description.length}/500
        </span>
      </div>

      {/* Category */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: 'var(--text-primary, #111827)' }}>
          Category
        </label>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as CommunityCategory)}
          style={{
            width: '100%',
            padding: '12px',
            border: '1.5px solid var(--border-color, #d1d5db)',
            borderRadius: '10px',
            fontSize: '14px',
            outline: 'none',
            background: 'var(--bg-secondary, #f9fafb)',
            color: 'var(--text-primary, #111827)',
            boxSizing: 'border-box',
            cursor: 'pointer',
          }}
        >
          {COMMUNITY_CATEGORIES.map(c => (
            <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
          ))}
        </select>
      </div>

      {/* Visibility */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: 'var(--text-primary, #111827)' }}>
          Visibility
        </label>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={() => setVisibility('public')}
            style={{
              flex: 1,
              padding: '12px',
              background: visibility === 'public' ? 'var(--accent-light, #f5f3ff)' : 'var(--bg-secondary, #f9fafb)',
              border: `1.5px solid ${visibility === 'public' ? 'var(--accent, #7c3aed)' : 'var(--border-color, #e5e7eb)'}`,
              borderRadius: '10px',
              cursor: 'pointer',
              textAlign: 'center',
              color: visibility === 'public' ? 'var(--accent, #7c3aed)' : 'var(--text-secondary, #6b7280)',
              fontWeight: visibility === 'public' ? '600' : '400',
            }}
          >
            <Globe size={16} style={{ marginBottom: '4px' }} />
            <div style={{ fontSize: '13px' }}>Public</div>
          </button>
          <button
            onClick={() => setVisibility('private')}
            style={{
              flex: 1,
              padding: '12px',
              background: visibility === 'private' ? 'var(--accent-light, #f5f3ff)' : 'var(--bg-secondary, #f9fafb)',
              border: `1.5px solid ${visibility === 'private' ? 'var(--accent, #7c3aed)' : 'var(--border-color, #e5e7eb)'}`,
              borderRadius: '10px',
              cursor: 'pointer',
              textAlign: 'center',
              color: visibility === 'private' ? 'var(--accent, #7c3aed)' : 'var(--text-secondary, #6b7280)',
              fontWeight: visibility === 'private' ? '600' : '400',
            }}
          >
            <Lock size={16} style={{ marginBottom: '4px' }} />
            <div style={{ fontSize: '13px' }}>Private</div>
          </button>
        </div>
      </div>

      {/* Monetization */}
      {permissions.canMonetize && (
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={isMonetized}
              onChange={(e) => setIsMonetized(e.target.checked)}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary, #111827)' }}>
              <DollarSign size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
              Monetize this community (you keep 85%)
            </span>
          </label>
          {isMonetized && (
            <div style={{ marginTop: '10px', marginLeft: '28px' }}>
              <label style={{ fontSize: '12px', color: 'var(--text-secondary, #6b7280)', marginBottom: '4px', display: 'block' }}>
                Monthly subscription price (USD)
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary, #111827)' }}>$</span>
                <input
                  type="number"
                  value={subscriptionPrice}
                  onChange={(e) => setSubscriptionPrice(Math.max(0, parseInt(e.target.value) || 0))}
                  min={0}
                  step={1}
                  placeholder="29"
                  style={{
                    width: '120px',
                    padding: '10px',
                    border: '1.5px solid var(--border-color, #d1d5db)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    background: 'var(--bg-secondary, #f9fafb)',
                    color: 'var(--text-primary, #111827)',
                  }}
                />
                <span style={{ fontSize: '13px', color: 'var(--text-muted, #9ca3af)' }}>/ month</span>
              </div>
              {subscriptionPrice > 0 && (
                <div style={{ marginTop: '8px', padding: '8px 12px', background: '#fef3c7', borderRadius: '8px', fontSize: '12px', color: '#92400e' }}>
                  💰 You'll earn <strong>${(subscriptionPrice * 0.85).toFixed(2)}</strong> per subscriber/month.
                  Platform fee: <strong>${(subscriptionPrice * 0.15).toFixed(2)}</strong> (15%)
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Tags */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px', color: 'var(--text-primary, #111827)' }}>
          Tags (comma-separated)
        </label>
        <input
          type="text"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder="e.g., marketing, social-media, growth"
          style={{
            width: '100%',
            padding: '12px',
            border: '1.5px solid var(--border-color, #d1d5db)',
            borderRadius: '10px',
            fontSize: '14px',
            outline: 'none',
            background: 'var(--bg-secondary, #f9fafb)',
            color: 'var(--text-primary, #111827)',
            boxSizing: 'border-box',
          }}
        />
      </div>
    </Modal>
  );
});

CreateCommunityModal.displayName = 'CreateCommunityModal';

// ─── EARNINGS DASHBOARD ────────────────────
export const EarningsDashboard: React.FC<{
  communityId: string;
  earningsSummary: EarningsSummary | null;
  loading: boolean;
}> = memo(({ communityId, earningsSummary, loading }) => {
  const { formatCurrency } = useCommission();

  if (loading) {
    return (
      <div style={{ padding: '20px' }}>
        <Skeleton height="200px" />
      </div>
    );
  }

  if (!earningsSummary) {
    return (
      <EmptyState
        icon={<Coins size={48} />}
        title="No earnings yet"
        description="Start monetizing your community to see earnings here."
      />
    );
  }

  return (
    <div style={{
      padding: '20px',
      background: 'var(--bg-primary, white)',
      borderRadius: '14px',
      border: '1px solid var(--border-color, #e5e7eb)',
    }}>
      <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px', color: 'var(--text-primary, #111827)' }}>
        <DollarSign size={18} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
        Earnings Dashboard
      </h3>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        <EarningsCard
          title="Total Revenue"
          value={formatCurrency(earningsSummary.totalRevenue)}
          color="#7c3aed"
        />
        <EarningsCard
          title="Your Earnings (85%)"
          value={formatCurrency(earningsSummary.ownerEarnings)}
          color="#22c55e"
        />
        <EarningsCard
          title="Platform Fee (15%)"
          value={formatCurrency(earningsSummary.platformCommission)}
          color="#f59e0b"
        />
        <EarningsCard
          title="Subscribers"
          value={String(earningsSummary.subscriberCount)}
          color="#3b82f6"
        />
      </div>

      {/* Recent Transactions */}
      {earningsSummary.transactions && earningsSummary.transactions.length > 0 && (
        <div>
          <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '10px', color: 'var(--text-primary, #111827)' }}>
            Recent Transactions
          </h4>
          {earningsSummary.transactions.slice(0, 5).map(tx => (
            <div
              key={tx.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 0',
                borderBottom: '1px solid var(--border-color-light, #f3f4f6)',
                fontSize: '13px',
              }}
            >
              <div>
                <span style={{ color: 'var(--text-primary, #111827)' }}>
                  {formatCurrency(tx.amount)}
                </span>
                <span style={{ color: 'var(--text-muted, #9ca3af)', marginLeft: '8px' }}>
                  {formatRelativeTime(tx.date)}
                </span>
              </div>
              <Badge
                text={tx.status.charAt(0).toUpperCase() + tx.status.slice(1)}
                color={tx.status === 'completed' ? '#065f46' : '#92400e'}
                bgColor={tx.status === 'completed' ? '#ecfdf5' : '#fef3c7'}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

EarningsDashboard.displayName = 'EarningsDashboard';

// ─── EARNINGS CARD ─────────────────────────
const EarningsCard: React.FC<{ title: string; value: string; color: string }> = memo(({ title, value, color }) => (
  <div style={{
    padding: '16px',
    background: 'var(--bg-secondary, #f9fafb)',
    borderRadius: '12px',
    borderLeft: `3px solid ${color}`,
  }}>
    <p style={{ fontSize: '11px', color: 'var(--text-muted, #9ca3af)', marginBottom: '4px', textTransform: 'uppercase', fontWeight: '600' }}>
      {title}
    </p>
    <p style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text-primary, #111827)', margin: 0 }}>
      {value}
    </p>
  </div>
));

EarningsCard.displayName = 'EarningsCard';

// ─── SOCIAL LINKS MANAGER ──────────────────
export const SocialLinksManager: React.FC<{
  socialLinks: SocialLink[];
  userPlan: string;
  onUpdate: (links: SocialLink[]) => void;
}> = memo(({ socialLinks, userPlan, onUpdate }) => {
  const { allowedPlatforms, lockedPlatforms, platformLabels } = useSocialPlatforms(userPlan);
  const [editingPlatform, setEditingPlatform] = useState<SocialPlatform | null>(null);
  const [editUrl, setEditUrl] = useState('');

  const handleSave = (platform: SocialPlatform) => {
    if (!editUrl.trim()) return;
    const updated = socialLinks.filter(s => s.platform !== platform);
    updated.push({
      platform,
      url: editUrl.trim(),
      connected: true,
      lastSynced: new Date().toISOString(),
    });
    onUpdate(updated);
    setEditingPlatform(null);
    setEditUrl('');
  };

  const handleRemove = (platform: SocialPlatform) => {
    const updated = socialLinks.filter(s => s.platform !== platform);
    onUpdate(updated);
  };

  const getLink = (platform: SocialPlatform): SocialLink | undefined => {
    return socialLinks.find(s => s.platform === platform);
  };

  return (
    <div style={{ padding: '20px', background: 'var(--bg-primary, white)', borderRadius: '14px', border: '1px solid var(--border-color, #e5e7eb)' }}>
      <h3 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px', color: 'var(--text-primary, #111827)' }}>
        <Link2 size={18} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
        Social Media Links
      </h3>

      {/* Allowed platforms */}
      {allowedPlatforms.map(platform => {
        const link = getLink(platform);
        return (
          <div key={platform} style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px', background: 'var(--bg-secondary, #f9fafb)', borderRadius: '10px' }}>
              <span style={{ fontWeight: '600', fontSize: '14px', color: 'var(--text-primary, #111827)' }}>
                {platformLabels[platform]}
              </span>
              {link?.connected ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Badge text="Connected" color="#065f46" bgColor="#ecfdf5" icon={<CheckCircle size={10} />} />
                  <button onClick={() => handleRemove(platform)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}>
                    <X size={16} />
                  </button>
                </div>
              ) : editingPlatform === platform ? (
                <div style={{ display: 'flex', gap: '6px' }}>
                  <input
                    type="url"
                    value={editUrl}
                    onChange={(e) => setEditUrl(e.target.value)}
                    placeholder="https://..."
                    style={{
                      padding: '6px 10px',
                      border: '1px solid var(--border-color, #e5e7eb)',
                      borderRadius: '6px',
                      fontSize: '12px',
                      width: '160px',
                    }}
                    autoFocus
                  />
                  <button onClick={() => handleSave(platform)} style={{ padding: '4px 10px', background: 'var(--accent, #7c3aed)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}>
                    Save
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => { setEditingPlatform(platform); setEditUrl(''); }}
                  style={{ padding: '6px 14px', background: 'var(--accent, #7c3aed)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}
                >
                  + Connect
                </button>
              )}
            </div>
          </div>
        );
      })}

      {/* Locked platforms (upsell) */}
      {lockedPlatforms.length > 0 && (
        <div style={{ marginTop: '20px' }}>
          <p style={{ fontSize: '12px', color: 'var(--text-muted, #9ca3af)', marginBottom: '10px' }}>
            🔒 Available with Enterprise plan:
          </p>
          {lockedPlatforms.map(platform => (
            <div
              key={platform}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px',
                background: 'var(--bg-secondary, #f3f4f6)',
                borderRadius: '10px',
                marginBottom: '8px',
                opacity: 0.6,
              }}
            >
              <span style={{ fontWeight: '600', fontSize: '14px', color: 'var(--text-secondary, #6b7280)' }}>
                {platformLabels[platform]}
              </span>
              <Badge text="Enterprise" color="#7c3aed" bgColor="#ede9fe" icon={<Lock size={10} />} />
            </div>
          ))}
          <button
            style={{
              width: '100%',
              padding: '10px',
              background: 'linear-gradient(135deg, #7c3aed, #6366f1)',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '13px',
              marginTop: '8px',
            }}
          >
            Upgrade to Enterprise — Unlock All Platforms
          </button>
        </div>
      )}
    </div>
  );
});

SocialLinksManager.displayName = 'SocialLinksManager';

// ─── EXPORT ALL ────────────────────────────
export default {
  Toast,
  ToastContainer,
  Avatar,
  Badge,
  Skeleton,
  EmptyState,
  Modal,
  CommunityCard,
  PostCard,
  PollDisplay,
  EventDisplay,
  SearchFilterBar,
  SortBar,
  CreateCommunityModal,
  EarningsDashboard,
  SocialLinksManager,
}; 